import React, { useState, useEffect } from 'react';
import { B } from '../constants';
import { promos as promosAPI } from '../api';
import { promoShareUrl } from '../lib/shareLinks';
import Modal from './ui/Modal';
import SocialPublishPanel from './SocialPublishPanel';

function BrandIcon({ id, color }) {
  return <img src={`https://cdn.simpleicons.org/${id}/${color.replace('#', '')}`} alt="" width="22" height="22" style={{ display: 'block' }} />;
}

const fallbackIcons = {
  sms: <svg viewBox="0 0 24 24" fill="none" width="22" height="22"><path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2v10z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round"/><path d="M8 9h8M8 13h6" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/></svg>,
  copy: <svg viewBox="0 0 24 24" fill="none" width="22" height="22"><path d="M10 13a5 5 0 007.5 3.5L21 13a5 5 0 00-7-7l-2 2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/><path d="M14 11a5 5 0 00-7.5-3.5L3 11a5 5 0 007 7l2-2" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>,
};

const PLATFORMS = [
  {
    id: 'instagram',
    label: 'Instagram',
    si: 'instagram',
    color: '#E4405F',
    shareUrl: (url, text) => `https://www.instagram.com/create/story/?url=${encodeURIComponent(url)}`,
  },
  {
    id: 'whatsapp',
    label: 'WhatsApp',
    si: 'whatsapp',
    color: '#25D366',
    shareUrl: (url, text) => `https://wa.me/?text=${encodeURIComponent(`${text}\n${url}`)}`,
  },
  {
    id: 'facebook',
    label: 'Facebook',
    si: 'facebook',
    color: '#1877F2',
    shareUrl: (url, text) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
  },
  {
    id: 'snapchat',
    label: 'Snapchat',
    si: 'snapchat',
    color: '#FFFC00',
    shareUrl: () => '',
  },
  {
    id: 'twitter',
    label: 'X',
    si: 'x',
    color: '#000',
    shareUrl: (url, text) => `https://x.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(url)}`,
  },
  {
    id: 'telegram',
    label: 'Telegram',
    si: 'telegram',
    color: '#0088CC',
    shareUrl: (url, text) => `https://t.me/share/url?url=${encodeURIComponent(url)}&text=${encodeURIComponent(text)}`,
  },
  {
    id: 'sms',
    label: 'Messages',
    si: null,
    color: '#34C759',
    shareUrl: (url, text) => `sms:?body=${encodeURIComponent(`${text}\n${url}`)}`,
  },
  {
    id: 'copy',
    label: 'Copy Link',
    si: null,
    color: B.forest,
    shareUrl: () => '',
  },
];

export default function PlatformShareModal({ promo, onClose, user, title, showToast }) {
  const [data, setData] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [sending, setSending] = useState(false);
  const [showDownload, setShowDownload] = useState(false);
  const modalTitle = title || (promo.videoUrl ? 'Share Reel' : 'Share Promo');
  const toast = showToast || (() => {});
  const isMobile = typeof navigator !== 'undefined' && /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

  useEffect(() => {
    if (promo.videoUrl) return;
    promosAPI.getVariants(promo.id)
      .then(result => { if (result?.variants) setData(result); })
      .catch(() => {});
  }, [promo.id, promo.videoUrl]);

  const pageUrl = promoShareUrl(promo.id);
  const shareText = promo.caption || `Check out "${promo.title}" on GreatBreak!`;

  const markCopied = (platformId) => {
    setCopiedId(platformId);
    setTimeout(() => setCopiedId(null), 2000);
    if (user) promosAPI.trackShare(promo.id, platformId).catch(() => {});
  };

  const handleShare = async (platform) => {
    if (platform.id === 'copy') {
      try { await navigator.clipboard.writeText(pageUrl); } catch {}
      markCopied('copy');
      return;
    }

    // Mobile: try video share for Instagram, then fall back to native share sheet
    if (isMobile && navigator.share) {
      if (platform.id === 'instagram') {
        setSending(true);
        const videoUrl = promo.videoUrl || data?.variants?.insta_reel?.url;
        if (videoUrl) {
          try {
            const response = await fetch(videoUrl);
            const blob = await response.blob();
            const file = new File([blob], `greatbreak-reel-${promo.id}.mp4`, { type: 'video/mp4' });
            await navigator.share({ title: promo.title, text: shareText, files: [file] });
            toast('Shared to Instagram!');
            setSending(false);
            markCopied('instagram');
            return;
          } catch (err) {
            if (err.name !== 'AbortError') console.error('Instagram video share failed:', err);
          }
        }
        setSending(false);
      }
      try {
        await navigator.share({ title: promo.title, text: shareText, url: pageUrl });
        markCopied(platform.id);
        return;
      } catch (err) {
        if (err.name !== 'AbortError') console.error('Native share failed:', err);
        return;
      }
    }

    // Desktop: open web sharer URLs directly
    const webUrl = platform.shareUrl(pageUrl, shareText);
    if (webUrl && webUrl.startsWith('http')) {
      window.open(webUrl, '_blank', 'noopener,noreferrer');
    } else {
      toast(`Open ${platform.label} on your phone to share this link: ${pageUrl}`);
    }
    markCopied(platform.id);
  };

  return (
    <Modal isOpen onClose={onClose} title={modalTitle} size="sm" ariaLabel={modalTitle}>
      <p style={{ fontSize: 14, color: B.muted, marginBottom: 16, lineHeight: 1.4 }}>
        Share "<strong>{promo.title}</strong>"
      </p>

      {user && (promo.userId ?? promo.user?.id) === user.id && promo.videoSource === 's3' && (
        <SocialPublishPanel promo={promo} toast={toast} />
      )}

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 0, marginBottom: 4 }}>
        {PLATFORMS.map(platform => {
          const isCopied = copiedId === platform.id;
          return (
            <div key={platform.id} onClick={() => !sending && handleShare(platform)}
              style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4, padding: '12px 4px', cursor: sending ? 'wait' : 'pointer', borderRadius: 12, transition: 'all 0.15s', userSelect: 'none', position: 'relative' }}>
              <div style={{ width: 52, height: 52, borderRadius: '50%', background: isCopied ? 'white' : `${platform.color}15`, display: 'flex', alignItems: 'center', justifyContent: 'center', color: isCopied ? B.forest : platform.color, transition: 'all 0.15s', transform: isCopied ? 'scale(1.1)' : 'scale(1)' }}>
                {isCopied
                  ? <svg viewBox="0 0 24 24" fill="none" width="22" height="22"><path d="M5 13l4 4L19 7" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  : platform.si ? <BrandIcon id={platform.si} color={platform.color} /> : fallbackIcons[platform.id]
                }
                {platform.id === 'instagram' && sending && (
                  <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.35)', borderRadius: '50%' }}>
                    <div style={{ width: 16, height: 16, border: '2px solid #fff', borderTopColor: 'transparent', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
                  </div>
                )}
              </div>
              <span style={{ fontSize: 10, fontWeight: 600, color: isCopied ? B.forest : B.muted, textAlign: 'center', lineHeight: 1.2 }}>
                {isCopied ? 'Copied!' : platform.label}
              </span>
            </div>
          );
        })}
      </div>

      <p style={{ fontSize: 11, color: B.muted, textAlign: 'center', marginTop: 12, marginBottom: 0 }}>
        {sending ? 'Preparing video for Instagram…' : isMobile ? 'Tap any platform to share. Native share opens the app directly.' : 'Tap any platform to share directly.'}
      </p>

      {data?.variants && Object.keys(data.variants).length > 0 && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 20, marginBottom: 8, cursor: 'pointer' }}
            onClick={() => setShowDownload(!showDownload)}>
            <span style={{ fontSize: 14, fontWeight: 600, color: B.muted, flex: 1 }}>Download videos</span>
            <span style={{ fontSize: 12, color: B.muted }}>{showDownload ? '▲' : '▼'}</span>
          </div>
          {showDownload && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {Object.entries(data.variants).map(([key, variant]) => (
                <a key={key} href={variant.url} download
                  style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', borderRadius: 12, background: B.sand, textDecoration: 'none', color: 'inherit', fontSize: 13, fontWeight: 600 }}>
                  <span style={{ fontSize: 16 }}>⬇️</span>
                  {key} — {variant.width}×{variant.height}
                </a>
              ))}
            </div>
          )}
        </>
      )}
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </Modal>
  );
}
